import * as React from 'react';
import {AlertCircle, Check, TriangleAlert, XCircle} from 'lucide-react';
import {cn} from '../lib/cn';

export enum AlertType {
  INFO = 'INFO',
  WARNING = 'WARNING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  LIGHT = 'LIGHT',
}

export enum AlertSize {
  S = 'S',
  M = 'M',
  L = 'L',
}

const getIcon = (type: AlertType) => {
  const key = type.toLowerCase();
  const iconProps = {
    className: 'relative',
    stroke: `var(--alert-box-${key}-icon)`,
  };
  return {
    [AlertType.INFO]: <AlertCircle {...iconProps} />,
    [AlertType.WARNING]: <TriangleAlert {...iconProps} />,
    [AlertType.ERROR]: <XCircle {...iconProps} />,
    [AlertType.LIGHT]: null,
    [AlertType.SUCCESS]: <Check {...iconProps} />,
  }[type];
};

const sizeClasses: Record<AlertSize, string> = {
  [AlertSize.S]: 'px-3 py-3 text-sm font-medium items-start',
  [AlertSize.M]: 'p-4 text-base font-medium items-start',
  [AlertSize.L]: 'px-6 py-5 items-start',
};

const typeStyles: Record<AlertType, string> = {
  [AlertType.INFO]:
    'border-[color:var(--alert-box-info-border)] bg-[var(--alert-box-info-bg)] text-[color:var(--alert-box-info-text)]',
  [AlertType.WARNING]:
    'border-[color:var(--alert-box-warning-border)] bg-[var(--alert-box-warning-bg)] text-[color:var(--alert-box-warning-text)]',
  [AlertType.ERROR]:
    'border-[color:var(--alert-box-error-border)] bg-[var(--alert-box-error-bg)] text-[color:var(--alert-box-error-text)]',
  [AlertType.SUCCESS]:
    'border-[color:var(--alert-box-success-border)] bg-[var(--alert-box-success-bg)] text-[color:var(--alert-box-success-text)]',
  [AlertType.LIGHT]:
    'border-[color:var(--alert-box-light-border)] bg-[var(--alert-box-light-bg)] text-[color:var(--alert-box-light-text)]',
};

export interface AlertBoxProps {
  children: React.ReactNode;
  type?: AlertType;
  size?: AlertSize;
  withIcon?: boolean;
  className?: string;
}

export function AlertBox({
  children,
  className = '',
  withIcon = true,
  size = AlertSize.L,
  type = AlertType.INFO,
}: AlertBoxProps) {
  return (
    <div
      data-slot="alert-box"
      className={cn(
        'flex gap-3 rounded-[var(--alert-box-radius)] border-solid border-[length:var(--alert-box-border-width)] [&_svg]:h-5 [&_svg]:w-5 [&_svg]:shrink-0',
        sizeClasses[size],
        typeStyles[type],
        className,
      )}
    >
      {withIcon && getIcon(type)}
      <div className="max-w-4xl text-left font-medium lg:max-w-full">{children}</div>
    </div>
  );
}

AlertBox.displayName = 'AlertBox';

export const AlertTypes = AlertType;
export const AlertSizes = AlertSize;
